import { FormControl, FormHelperText, TextField } from '@mui/material'
import React, { useEffect, useMemo, useState, type ReactElement } from 'react'

type Props = {
  id?: string
  value: string
  label: string
  labelWidth?: number
  onChange: (value: string) => void
  validation?: boolean
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  multiline?: boolean
  showError?: boolean
}

const ensureProtocol = (candidate: string): string => {
  if (/^https?:\/\//i.test(candidate)) {
    return candidate
  }
  return `https://${candidate}`
}

const hasDomainSuffix = (hostname: string): boolean => {
  // Accept hostnames containing at least one dot and alpha characters after the final dot.
  // This filters out obviously invalid hostnames like "https://foo/" while still accepting subdomains.
  if (!hostname) return false
  const parts = hostname.split('.')
  if (parts.length < 2) return false
  const tld = parts[parts.length - 1]
  return /^[a-z]{2,}$/i.test(tld)
}

const isValidUrlOrDomain = (value: string): boolean => {
  if (!value) return true

  try {
    const candidateWithProtocol = ensureProtocol(value.trim())
    const parsed = new URL(candidateWithProtocol)
    return hasDomainSuffix(parsed.hostname)
  } catch {
    return false
  }
}

export default function UrlInput({
  id,
  label,
  value = '',
  onChange,
  disabled = false,
  required = false,
  errorMessage = '',
  validation,
  multiline,
  showError = true,
}: Props): ReactElement {
  const [error, setError] = useState<boolean>(showError && validation === true && value === '')

  useEffect(() => {
    if (showError) {
      validation === true && value === '' ? setError(true) : setError(false)
    } else {
      setError(false)
    }
  }, [showError, value, validation])

  const isCurrentValueValid = useMemo(() => isValidUrlOrDomain(value), [value])

  return (
    <FormControl fullWidth variant="outlined">
      <TextField
        id={id || label}
        variant="outlined"
        multiline={multiline}
        rows={multiline ? 4 : 1}
        label={label}
        value={value || ''}
        onChange={event => {
          event.currentTarget.value !== '' || validation !== true ? setError(false) : setError(true)
          onChange(event.currentTarget.value)
        }}
        autoComplete="url"
        required={required}
        disabled={disabled}
        error={error || !isCurrentValueValid}
        aria-describedby="url-input"
      />
      {(error || (!isCurrentValueValid && value)) && (
        <FormHelperText error variant="outlined">
          {errorMessage || 'Bitte gib eine gültige URL oder Domain ein.'}
        </FormHelperText>
      )}
    </FormControl>
  )
}

